import { oc } from "@orpc/contract";
import { z } from "zod";
import { FieldSelectOptionSchema } from "@erp/shared-types";

const FieldControlSchema = z.enum([
  "text",
  "select",
  "date",
  "hidden",
  "readonly",
  "textarea",
]);

/**
 * Persisted field entry schema — properties that are stored in the DB config.
 * Used for upsert input validation to prevent runtime-only fields from being persisted.
 */
export const PersistedFieldEntrySchema = z.object({
  id: z.string().min(1),
  // Empty labels are valid: status / actions columns render a blank header.
  label: z.string(),
  // Optional: resolver-driven fields (e.g. extracted_data_field) carry no dataPath.
  dataPath: z.string().min(1).optional(),
  format: z
    .enum([
      "string",
      "number",
      "integer",
      "float",
      "currency",
      "date",
      "boolean",
    ])
    .optional(),
  sortable: z.boolean().optional(),
  filterable: z.boolean().optional(),
  alignRight: z.boolean().optional(),
  hidden: z.boolean().optional(),
  enabled: z.boolean().optional(),
  hideWhenEmpty: z.boolean().optional(),
  editable: z.boolean().optional(),
  /** Whether the field belongs to the header section or the line-item table. */
  fieldGroup: z.enum(["header", "lineItem"]).optional(),
  /** Editor control type — persisted (drives how the field renders/edits). */
  control: FieldControlSchema.optional(),
  widget: z.string().optional(),
  options: z.record(z.string(), z.unknown()).optional(),
  // --- Resolver ---
  resolver: z.string().min(1).optional(),
});

/**
 * Full field entry schema — adds the truly runtime-only fields injected by
 * backend resolvers (never persisted). Used in GET responses where the backend
 * may enrich fields with resolved values.
 */
export const FieldEntrySchema = PersistedFieldEntrySchema.extend({
  resolvedValue: z.unknown().optional(),
  selectOptions: z.array(FieldSelectOptionSchema).optional(),
});

// ---------------------------------------------------------------------------
// LayoutNode — recursive tree for spatial arrangement
// ---------------------------------------------------------------------------

const FieldLayoutNodeSchema = z.object({
  type: z.literal("field"),
  fieldId: z.string().min(1),
});

const WidgetLayoutNodeSchema = z.object({
  type: z.literal("widget"),
  widget: z.string().min(1),
});

type LayoutNodeInput =
  | z.infer<typeof FieldLayoutNodeSchema>
  | z.infer<typeof WidgetLayoutNodeSchema>
  | { type: "row"; children: LayoutNodeInput[] }
  | { type: "column"; children: LayoutNodeInput[] }
  | {
      type: "group";
      label: string;
      collapsible?: boolean;
      children: LayoutNodeInput[];
    };

const LayoutNodeSchema: z.ZodType<LayoutNodeInput> = z.lazy(() =>
  z.discriminatedUnion("type", [
    FieldLayoutNodeSchema,
    WidgetLayoutNodeSchema,
    z.object({
      type: z.literal("row"),
      children: z.array(LayoutNodeSchema),
    }),
    z.object({
      type: z.literal("column"),
      children: z.array(LayoutNodeSchema),
    }),
    z.object({
      type: z.literal("group"),
      label: z.string().min(1),
      collapsible: z.boolean().optional(),
      children: z.array(LayoutNodeSchema),
    }),
  ]),
);

export { LayoutNodeSchema };

// ---------------------------------------------------------------------------
// Contracts
// ---------------------------------------------------------------------------

const SectionTypeSchema = z.enum(["table", "card", "list", "form"]);

export const GetUiSectionConfigInputSchema = z.object({
  sectionId: z.string().min(1),
});

export const GetUiSectionConfigResponseSchema = z
  .object({
    sectionType: SectionTypeSchema,
    fields: z.array(FieldEntrySchema),
    layout: LayoutNodeSchema.nullable().optional(),
    config: z.record(z.string(), z.unknown()).nullable().optional(),
  })
  .nullable();

export const UpsertUiSectionConfigInputSchema = z.object({
  sectionId: z.string().min(1),
  sectionType: SectionTypeSchema,
  // Optional: only the keys provided are written. A caller that manages just the
  // `config` bag (e.g. the review-page editor) omits `fields`/`layout`, and the
  // handler preserves the existing values — so it can't clobber the legacy fields.
  fields: z.array(PersistedFieldEntrySchema).optional(),
  layout: LayoutNodeSchema.nullable().optional(),
  config: z.record(z.string(), z.unknown()).nullable().optional(),
});

export const UpsertUiSectionConfigResponseSchema = z.object({
  sectionType: SectionTypeSchema,
  fields: z.array(PersistedFieldEntrySchema),
  layout: LayoutNodeSchema.nullable().optional(),
  config: z.record(z.string(), z.unknown()).nullable().optional(),
});

/**
 * Discovery catalog for the configurable-UI editors: the code-registered field
 * resolvers + extractors an admin can assign to a field, surfaced so resolver
 * selection lives in the UI instead of in code.
 */
export const GetUiConfigCatalogResponseSchema = z.object({
  resolvers: z.array(z.string()),
  /** Subset of `resolvers` that are org-defined (DB-stored) rather than built-in. */
  customResolvers: z.array(z.string()),
  extractors: z.array(z.string()),
  /**
   * Code-derived dropdown options for select-capable header fields (orderType /
   * warehouse / shippingMethod / freightTerms), keyed by field id. Powers the
   * field editor's "Load current options" prefill.
   */
  selectOptionSources: z.record(
    z.string(),
    z.array(z.object({ value: z.string(), label: z.string() })),
  ),
});

export const orgUiSectionConfigsContracts = {
  get: oc
    .input(GetUiSectionConfigInputSchema)
    .output(GetUiSectionConfigResponseSchema)
    .route({
      method: "GET",
      summary: "Get a page section's field layout",
      description:
        "Returns the organization's configured fields, layout tree and settings for one table, card, list or form section, with resolved field values and dropdown options.",
    }),
  upsert: oc
    .input(UpsertUiSectionConfigInputSchema)
    .output(UpsertUiSectionConfigResponseSchema)
    .route({
      method: "POST",
      summary: "Save a page section's field layout",
      description:
        "Saves the organization's field list, layout or settings for one page section; omitted parts are kept. Requires permission to manage staff sections.",
    }),
  getCatalog: oc
    .input(z.object({}))
    .output(GetUiConfigCatalogResponseSchema)
    .route({
      method: "GET",
      summary: "List field resolvers and extractors for layouts",
      description:
        "Returns the built-in and custom field resolvers, extractors, and dropdown option sources an admin can assign when configuring page section fields.",
    }),
} as const;

export type GetUiConfigCatalogResponse = z.infer<
  typeof GetUiConfigCatalogResponseSchema
>;

export type UiSectionFieldEntry = z.infer<typeof FieldEntrySchema>;
export type GetUiSectionConfigInput = z.infer<
  typeof GetUiSectionConfigInputSchema
>;
export type GetUiSectionConfigResponse = z.infer<
  typeof GetUiSectionConfigResponseSchema
>;
export type UpsertUiSectionConfigInput = z.infer<
  typeof UpsertUiSectionConfigInputSchema
>;
export type UpsertUiSectionConfigResponse = z.infer<
  typeof UpsertUiSectionConfigResponseSchema
>;
